/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useState } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/cart/components/OrderSummary/OrderSummary.css';
import { useText } from '@adobe-commerce/elsie/i18n';
import { ChevronDown, ChevronUp } from '@adobe-commerce/elsie/icons';
import {
  Accordion,
  AccordionSection,
  Divider,
  ProgressSpinner,
} from '@adobe-commerce/elsie/components';
import { JSXInternal } from 'preact/src/jsx';
import { OrderSummaryLine } from '@/cart/containers/OrderSummaryLine/OrderSummaryLine';

export interface OrderSummaryLineItem {
  key: string;
  title?: string;
  className?: string;
  sortOrder: number;
  content:
    | string
    | JSXInternal.Element
    | VNode<HTMLAttributes<HTMLDivElement>>
    | OrderSummaryLineItem[]
    | undefined;
}

export interface OrderSummaryProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  variant?: 'primary' | 'secondary';
  heading?: VNode<HTMLAttributes<HTMLDivElement>>;
  loading?: boolean;
  subTotal?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceExcludingTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
    taxIncluded?: boolean;
    taxExcluded?: boolean;
    zeroTaxSubtotal?: boolean;
  };
  shipping?: VNode<HTMLAttributes<HTMLDivElement>>;
  discounts?: {
    label: string;
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    caption?: VNode<HTMLAttributes<HTMLDivElement>>;
    coupon?: VNode<HTMLAttributes<HTMLSpanElement>>;
  }[];
  taxTotal?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    estimated?: boolean;
  };
  taxesApplied?: {
    label: string;
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
  }[];
  total?: {
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    estimated?: boolean;
    priceWithoutTax?: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  printedCard?: {
    renderContent: boolean;
    taxIncluded: boolean;
    taxInclAndExcl: boolean;
    priceExclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceInclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  itemsGiftWrapping?: {
    renderContent: boolean;
    taxIncluded: boolean;
    taxInclAndExcl: boolean;
    priceExclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceInclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  orderGiftWrapping?: {
    renderContent: boolean;
    taxIncluded: boolean;
    taxInclAndExcl: boolean;
    priceExclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
    priceInclTax: VNode<HTMLAttributes<HTMLSpanElement>>;
  };
  primaryAction?: VNode<HTMLAttributes<HTMLButtonElement>>;
  coupons?: VNode<HTMLAttributes<HTMLDivElement>>;
  giftCards?: VNode<HTMLAttributes<HTMLDivElement>>;
  totalSaved?: VNode<HTMLAttributes<HTMLSpanElement>>;
  appliedGiftCards?: {
    label: VNode<HTMLAttributes<HTMLSpanElement>> | string;
    price: VNode<HTMLAttributes<HTMLSpanElement>>;
    content?: VNode[];
  };
  updateLineItems?: (
    lineItems: Array<OrderSummaryLineItem>
  ) => Array<OrderSummaryLineItem>;
}

export const OrderSummary: FunctionComponent<OrderSummaryProps> = ({
  className,
  children,
  variant = 'primary',
  heading,
  loading = true,
  subTotal,
  shipping,
  discounts,
  taxTotal,
  taxesApplied,
  total,
  primaryAction,
  coupons,
  giftCards,
  totalSaved,
  appliedGiftCards,
  printedCard,
  itemsGiftWrapping,
  orderGiftWrapping,
  updateLineItems = (lineItems) => lineItems,
  ...props
}) => {
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  const translations = useText({
    checkout: 'Cart.PriceSummary.checkout',
    discountedPrice: 'Cart.CartItem.discountedPrice',
    download: 'Cart.CartItem.download',
    heading: 'Cart.Cart.heading',
    message: 'Cart.CartItem.message',
    regularPrice: 'Cart.CartItem.regularPrice',
    recipient: 'Cart.CartItem.recipient',
    sender: 'Cart.CartItem.sender',
    file: 'Cart.CartItem.file',
    files: 'Cart.CartItem.files',

    orderSummary: 'Cart.PriceSummary.orderSummary',
    // Taxes
    taxesBreakdownTitle: 'Cart.PriceSummary.taxes.breakdown',
    taxTotal: 'Cart.PriceSummary.taxes.total',
    taxEstimated: 'Cart.PriceSummary.taxes.estimated',
    taxTotalOnly: 'Cart.PriceSummary.taxes.totalOnly',
    showTaxBreakdown: 'Cart.PriceSummary.taxes.showBreakdown',
    hideTaxBreakdown: 'Cart.PriceSummary.taxes.hideBreakdown',
    taxToBeDetermined: 'Cart.PriceSummary.taxToBeDetermined',

    // Subtotal
    subtotalLabel: 'Cart.PriceSummary.subTotal.label',
    subtotalWithTaxes: 'Cart.PriceSummary.subTotal.withTaxes',
    subtotalWithoutTaxes: 'Cart.PriceSummary.subTotal.withoutTaxes',

    // Total
    totalEstimated: 'Cart.PriceSummary.total.estimated',
    totalLabel: 'Cart.PriceSummary.total.label',
    totalWithoutTax: 'Cart.PriceSummary.total.withoutTax',
    totalSaved: 'Cart.PriceSummary.total.saved',

    // Shipping
    shippingLabel: 'Cart.PriceSummary.shipping.label',
    zipPlaceholder: 'Cart.PriceSummary.shipping.zipPlaceholder',
    editZipAction: 'Cart.PriceSummary.shipping.editZipAction',
    shippingWithTaxes: 'Cart.PriceSummary.shipping.withTaxes',
    shippingWithoutTaxes: 'Cart.PriceSummary.shipping.withoutTaxes',
    shippingEstimated: 'Cart.PriceSummary.shipping.estimated',
    shippingEstimatedByState: 'Cart.PriceSummary.shipping.alternateField.state',
    shippingEstimatedByZip: 'Cart.PriceSummary.shipping.alternateField.zip',
    destinationLinkAriaLabel:
      'Cart.PriceSummary.shipping.destinationLinkAriaLabel',
    applyButton: 'Cart.PriceSummary.estimatedShippingForm.apply.label',
    countryField: 'Cart.PriceSummary.estimatedShippingForm.country.placeholder',
    freeShipping: 'Cart.PriceSummary.freeShipping',
    stateField: 'Cart.PriceSummary.estimatedShippingForm.state.placeholder',
    zipField: 'Cart.PriceSummary.estimatedShippingForm.zip.placeholder',

    // Printed Card
    printedCardTitle: 'Cart.PriceSummary.giftOptionsTax.printedCard.title',
    printedCardInclTax: 'Cart.PriceSummary.giftOptionsTax.printedCard.inclTax',
    printedCardExclTax: 'Cart.PriceSummary.giftOptionsTax.printedCard.exclTax',

    // Gift Wrapping Item
    itemGiftWrappingTitle:
      'Cart.PriceSummary.giftOptionsTax.itemGiftWrapping.title',
    itemGiftWrappingInclTax:
      'Cart.PriceSummary.giftOptionsTax.itemGiftWrapping.inclTax',
    itemGiftWrappingExclTax:
      'Cart.PriceSummary.giftOptionsTax.itemGiftWrapping.exclTax',

    // Gift Wrapping Order
    orderGiftWrappingTitle:
      'Cart.PriceSummary.giftOptionsTax.orderGiftWrapping.title',
    orderGiftWrappingInclTax:
      'Cart.PriceSummary.giftOptionsTax.orderGiftWrapping.inclTax',
    orderGiftWrappingExclTax:
      'Cart.PriceSummary.giftOptionsTax.orderGiftWrapping.exclTax',
  });

  const appliedGiftCardsContent = appliedGiftCards ? (
    <OrderSummaryLine
      key={appliedGiftCards?.label}
      label={appliedGiftCards?.label}
      price={appliedGiftCards?.price}
      classSuffixes={['applied-gift-cards']}
    >
      {appliedGiftCards?.content}
    </OrderSummaryLine>
  ) : null;

  const subTotalContent = subTotal && (
    <OrderSummaryLine
      key={translations.subtotalLabel}
      label={translations.subtotalLabel}
      price={subTotal.price}
      classSuffixes={['subTotal']}
    >
      {subTotal.taxIncluded && (
        <div
          data-testid="sub-total-tax-caption"
          className="cart-order-summary__caption"
        >
          <span>{translations.subtotalWithTaxes}</span>
        </div>
      )}

      {subTotal.taxExcluded ? (
        <div
          data-testid="sub-total-tax-caption-excluded"
          className="cart-order-summary__caption"
        >
          <span>
            {subTotal.priceExcludingTax}
            &nbsp;
            {translations.subtotalWithoutTaxes}
          </span>
        </div>
      ) : undefined}
    </OrderSummaryLine>
  );

  const discountsContent = discounts && discounts.length > 0 && (
    <>
      {discounts.map((discount) => (
        <OrderSummaryLine
          key={discount.label}
          label={discount.label}
          price={discount.price}
          classSuffixes={['discount']}
        >
          {discount.coupon && (
            <VComponent
              node={discount.coupon}
              className="cart-order-summary__coupon__code"
            />
          )}

          {discount.caption && (
            <VComponent
              node={discount.caption}
              className="cart-order-summary__caption"
            />
          )}
        </OrderSummaryLine>
      ))}
    </>
  );

  const taxContent =
    taxesApplied && taxesApplied.length > 0 ? (
      /* Show breakdown if provided */
      <Accordion
        data-testid="tax-breakdown"
        className="cart-order-summary__taxes"
        iconOpen={ChevronDown}
        iconClose={ChevronUp}
      >
        <AccordionSection
          title={translations.taxesBreakdownTitle}
          secondaryText={
            !showTaxBreakdown && taxTotal ? (
              <VComponent
                node={taxTotal.price}
                className="cart-order-summary__price"
              />
            ) : undefined
          }
          renderContentWhenClosed={false}
          onStateChange={setShowTaxBreakdown}
        >
          <div className="cart-order-summary__appliedTaxes">
            {taxesApplied.map((tax) => (
              <OrderSummaryLine
                key={tax.label}
                label={tax.label}
                price={tax.price}
                classSuffixes={['taxEntry']}
                labelClassSuffix="muted"
              />
            ))}
          </div>

          {taxTotal && (
            <OrderSummaryLine
              label={translations.taxTotal}
              price={taxTotal.price}
              classSuffixes={['taxTotal']}
              labelClassSuffix="muted"
            />
          )}
        </AccordionSection>
      </Accordion>
    ) : (
      taxTotal && (
        <OrderSummaryLine
          label={
            taxTotal.estimated
              ? translations.taxEstimated
              : translations.taxTotalOnly
          }
          price={taxTotal.price}
          classSuffixes={['taxTotal']}
          testId="tax-total-only"
        />
      )
    );

  const printedCardContent = printedCard?.renderContent ? (
    <OrderSummaryLine
      key={translations.printedCardTitle}
      label={translations.printedCardTitle}
      price={
        printedCard.taxInclAndExcl || printedCard.taxIncluded
          ? printedCard.priceInclTax
          : printedCard.priceExclTax
      }
      classSuffixes={['printedCardContent']}
    >
      {printedCard.taxIncluded && (
        <div
          data-testid="printed-card-incl"
          className="cart-order-summary__caption"
        >
          <span>{translations.printedCardInclTax}</span>
        </div>
      )}

      {printedCard.taxInclAndExcl ? (
        <div
          data-testid="printed-card-incl-excl"
          className="cart-order-summary__caption"
        >
          <span>
            {printedCard.priceExclTax}
            &nbsp;
            {translations.printedCardExclTax}
          </span>
        </div>
      ) : undefined}
    </OrderSummaryLine>
  ) : null;

  const itemsGiftWrappingContent = itemsGiftWrapping?.renderContent ? (
    <OrderSummaryLine
      key={translations.itemGiftWrappingTitle}
      label={translations.itemGiftWrappingTitle}
      price={
        itemsGiftWrapping.taxInclAndExcl || itemsGiftWrapping.taxIncluded
          ? itemsGiftWrapping.priceInclTax
          : itemsGiftWrapping.priceExclTax
      }
      classSuffixes={['itemsGiftWrappingContent']}
    >
      {itemsGiftWrapping.taxIncluded ? (
        <div
          data-testid="item-gift-wrapping-incl"
          className="cart-order-summary__caption"
        >
          <span>{translations.itemGiftWrappingInclTax}</span>
        </div>
      ) : null}

      {itemsGiftWrapping.taxInclAndExcl ? (
        <div
          data-testid="item-gift-wrapping-incl-excl"
          className="cart-order-summary__caption"
        >
          <span>
            {itemsGiftWrapping.priceExclTax}
            &nbsp;
            {translations.itemGiftWrappingExclTax}
          </span>
        </div>
      ) : null}
    </OrderSummaryLine>
  ) : null;

  const orderGiftWrappingContent = orderGiftWrapping?.renderContent ? (
    <OrderSummaryLine
      key={translations.orderGiftWrappingTitle}
      label={translations.orderGiftWrappingTitle}
      price={
        orderGiftWrapping.taxInclAndExcl || orderGiftWrapping.taxIncluded
          ? orderGiftWrapping.priceInclTax
          : orderGiftWrapping.priceExclTax
      }
      classSuffixes={['orderGiftWrappingContent']}
    >
      {orderGiftWrapping.taxIncluded && (
        <div
          data-testid="order-gift-wrapping-incl"
          className="cart-order-summary__caption"
        >
          <span>{translations.orderGiftWrappingInclTax}</span>
        </div>
      )}

      {orderGiftWrapping.taxInclAndExcl ? (
        <div
          data-testid="order-gift-wrapping-incl-excl"
          className="cart-order-summary__caption"
        >
          <span>
            {orderGiftWrapping.priceExclTax}
            &nbsp;
            {translations.orderGiftWrappingExclTax}
          </span>
        </div>
      ) : undefined}
    </OrderSummaryLine>
  ) : null;

  const lineItems: Array<OrderSummaryLineItem> = [
    {
      key: 'subTotalContent',
      sortOrder: 100,
      content: subTotalContent,
    },
    ...(shipping
      ? [
          {
            key: 'shippingContent',
            sortOrder: 200,
            content: (
              <VComponent
                node={shipping}
                className="cart-order-summary__shipping"
              />
            ),
          },
        ]
      : []),
    {
      key: 'printedCard',
      sortOrder: 300,
      content: printedCardContent,
    },
    {
      key: 'itemsGiftWrappingContent',
      sortOrder: 400,
      content: itemsGiftWrappingContent,
    },
    {
      key: 'orderGiftWrappingContent',
      sortOrder: 500,
      content: orderGiftWrappingContent,
    },
    {
      key: 'discountsContent',
      sortOrder: 600,
      content: discountsContent,
    },
    {
      key: 'appliedGiftCardsContent',
      sortOrder: 700,
      content: appliedGiftCardsContent,
    },
    {
      key: 'taxContent',
      sortOrder: 800,
      content: taxContent,
    },
    ...(total
      ? [
          {
            key: 'taxContent',
            sortOrder: 900,
            content: (
              <OrderSummaryLine
                label={
                  total.estimated
                    ? translations.totalEstimated
                    : translations.totalLabel
                }
                price={total.price}
                classSuffixes={['total', 'total--padded']}
                testId="total-content"
                labelClassSuffix="bold"
              />
            ),
          },
        ]
      : []),
    ...(total?.priceWithoutTax
      ? [
          {
            key: 'totalWithoutTaxContent',
            sortOrder: 1000,
            content: (
              <OrderSummaryLine
                label={translations.totalWithoutTax}
                price={total.priceWithoutTax}
                classSuffixes={['totalWithoutTax']}
                testId="total-without-tax"
                labelClassSuffix="muted"
              />
            ),
          },
        ]
      : []),
    ...(totalSaved
      ? [
          {
            key: 'totalSavedContent',
            sortOrder: 1100,
            content: (
              <OrderSummaryLine
                label={translations.totalSaved}
                price={totalSaved}
                classSuffixes={['saved']}
                testId="total-saved"
                labelClassSuffix="muted"
              />
            ),
          },
        ]
      : []),
    ...(primaryAction
      ? [
          {
            key: 'primaryActionContent',
            sortOrder: 1200,
            content: (
              <div
                className={classes([
                  'cart-order-summary__entry',
                  'cart-order-summary__primaryAction',
                ])}
              >
                {primaryAction}
              </div>
            ),
          },
        ]
      : []),
    ...(coupons
      ? [
          {
            key: 'couponsContent',
            sortOrder: 1300,
            content: (
              <VComponent
                node={coupons}
                className="cart-order-summary__coupons"
              />
            ),
          },
        ]
      : []),
    ...(giftCards
      ? [
          {
            key: 'giftCardsContent',
            sortOrder: 1400,
            content: (
              <VComponent
                node={giftCards}
                className="cart-order-summary__gift-cards"
              />
            ),
          },
        ]
      : []),
  ];

  const updatedLineItems: Array<OrderSummaryLineItem> = updateLineItems(
    lineItems
  ).sort(
    (currentLine: OrderSummaryLineItem, nextLine: OrderSummaryLineItem) => {
      return currentLine.sortOrder - nextLine.sortOrder;
    }
  );

  return (
    <div
      {...props}
      className={classes([
        'cart-order-summary',
        ['cart-order-summary--loading', loading],
        [`cart-order-summary__${variant}`, variant],
        className,
      ])}
    >
      {loading && <ProgressSpinner className="cart-order-summary__spinner" />}

      <div className="cart-order-summary__heading">
        {heading && (
          <VComponent
            node={heading}
            className="cart-order-summary__heading-text"
          />
        )}

        <Divider
          variant="primary"
          className="cart-order-summary__divider-primary"
        />
      </div>

      <div className="cart-order-summary__content">
        {updatedLineItems.map((line: OrderSummaryLineItem) => {
          if (Array.isArray(line.content)) {
            return (
              <Accordion
                key={line.key}
                className={line.className}
                actionIconPosition={'right'}
                iconOpen={ChevronDown}
                iconClose={ChevronUp}
              >
                <AccordionSection
                  defaultOpen={false}
                  title={line.title}
                  renderContentWhenClosed={false}
                >
                  {line.content.map((innerLine) => {
                    return innerLine.content;
                  })}
                </AccordionSection>
              </Accordion>
            );
          }
          return line.content;
        })}
      </div>
    </div>
  );
};
